import { useForm } from "@tanstack/react-form";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { Button } from "@/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { resolveApiPath } from "@/lib/utils.ts";
import {
  approvePaymentPilotReq,
  type ApprovePaymentPilotReq,
  type PaymentPilotSummary,
} from "@/types/paymentPilot.ts";
import { api } from "@/lib/http.ts";
import type { LinkWalletMethodsResponse } from "@/types/linkWallet.ts";

const unresolved = new Set([
  "preparing",
  "authorizing",
  "awaitingProvider",
  "ready",
  "creatingBrowser",
  "submitting",
  "verifying",
]);

const paymentQueries = {
  methods: () =>
    queryOptions({
      queryKey: ["linkWalletMethods"],
      queryFn: async ({ signal }) =>
        (await api.get<LinkWalletMethodsResponse>("/api/a/payments/link/methods", { signal })).data,
      retry: false,
    }),
  attempt: (id: string) =>
    queryOptions({
      queryKey: ["paymentPilot", id],
      queryFn: async ({ signal }) =>
        (await api.get<PaymentPilotSummary>(`/api/a/payments/pilot/${id}`, { signal })).data,
      refetchInterval: (query) =>
        !query.state.error && query.state.data && unresolved.has(query.state.data.status)
          ? 3000
          : false,
      retry: false,
    }),
};

const approvePilotPurchase = async (id: string, data: ApprovePaymentPilotReq) =>
  (await api.post<PaymentPilotSummary>(`/api/a/payments/pilot/${id}/approve`, data)).data;

const cancelPilotPurchase = async (id: string) =>
  (await api.post<PaymentPilotSummary>(`/api/a/payments/pilot/${id}/cancel`, {})).data;

const billingFields = [
  {
    name: "billingName",
    label: "Name on your card",
    type: "text",
    autoComplete: "cc-name",
    maxLength: 255,
  },
  {
    name: "email",
    label: "Email for your receipt",
    type: "email",
    autoComplete: "email",
    maxLength: 255,
  },
  {
    name: "billingCountry",
    label: "Billing country (two-letter code)",
    type: "text",
    autoComplete: "country",
    maxLength: 2,
  },
  {
    name: "billingPostalCode",
    label: "Billing postal code (optional)",
    type: "text",
    autoComplete: "postal-code",
    maxLength: 32,
  },
] as const;

/** Shows the fixed purchase for user approval and reports its verified payment status. */
export const PaymentPilot = () => {
  const { attemptId = "" } = useParams<{ attemptId: string }>();
  const client = useQueryClient();
  const query = paymentQueries.attempt(attemptId);
  const attempt = useQuery(query);
  const methods = useQuery({
    ...paymentQueries.methods(),
    enabled: attempt.data?.status === "awaitingConfirmation",
  });
  const update = (data: PaymentPilotSummary) => client.setQueryData(query.queryKey, data);
  const approve = useMutation({
    mutationFn: (value: Parameters<typeof approvePilotPurchase>[1]) =>
      approvePilotPurchase(attemptId, value),
    onSuccess: update,
  });
  const cancel = useMutation({
    mutationFn: () => cancelPilotPurchase(attemptId),
    onSuccess: update,
  });
  const form = useForm({
    defaultValues: {
      paymentMethodId: "",
      billingName: "",
      email: "",
      billingCountry: "",
      billingPostalCode: "",
    },
    validators: {
      onSubmit: ({ value }) =>
        approvePaymentPilotReq.safeParse(value).success ? undefined : "Review the payment details.",
    },
    onSubmit: ({ value }) => {
      approve.mutate(approvePaymentPilotReq.parse(value));
    },
  });
  const purchase = attempt.data?.purchase;
  const complete = attempt.data && ["paid", "cancelled", "declined"].includes(attempt.data.status);

  return (
    <main className="mx-auto flex max-w-xl flex-col gap-5 p-6">
      <Link
        href={attempt.data ? `/threads/${attempt.data.threadId}` : "/"}
        className="text-sm text-muted-foreground"
      >
        Back to conversation
      </Link>
      <Card>
        <CardHeader>
          <CardTitle>Review your purchase</CardTitle>
          <CardDescription>Check the item and total before approving payment.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {attempt.isPending && <output>Loading your purchase…</output>}
          {attempt.isError && (
            <p role="alert" className="text-sm text-destructive">
              This purchase could not be loaded. Return to the conversation and open it again.
            </p>
          )}
          {purchase && (
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-muted-foreground">Merchant</dt>
                <dd className="font-medium">{purchase.merchant}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Item</dt>
                <dd>{purchase.item}</dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Total</dt>
                <dd className="text-xl font-semibold">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: purchase.currency,
                  }).format(purchase.amountMinor / 100)}
                </dd>
              </div>
            </dl>
          )}
          {attempt.data && <output className="text-sm">{attempt.data.message}</output>}
          {(approve.isError || cancel.isError) && (
            <p role="alert" className="text-sm text-destructive">
              We couldn't confirm the latest purchase state. Check its status before taking another
              action.
            </p>
          )}
          {!attempt.isError && attempt.data?.status === "awaitingConfirmation" && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void form.handleSubmit();
              }}
              className="space-y-4"
            >
              <form.Field name="paymentMethodId">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="payment-method">Payment method</Label>
                    <Select value={field.state.value} onValueChange={field.handleChange}>
                      <SelectTrigger id="payment-method">
                        <SelectValue placeholder="Choose a saved Link card" />
                      </SelectTrigger>
                      <SelectContent>
                        {methods.data?.methods.map((method) => (
                          <SelectItem key={method.id} value={method.id}>
                            {method.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
              {methods.isError && (
                <p role="alert" className="text-sm text-destructive">
                  Saved cards could not be loaded. Check your Link connection.
                </p>
              )}
              {methods.data?.methods.length === 0 && (
                <Link className="text-sm text-primary" href="/payments/link">
                  Connect an eligible card in Link
                </Link>
              )}
              {billingFields.map((input) => (
                <form.Field key={input.name} name={input.name}>
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor={input.name}>{input.label}</Label>
                      <Input
                        id={input.name}
                        type={input.type}
                        autoComplete={input.autoComplete}
                        maxLength={input.maxLength}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(event) => field.handleChange(event.target.value)}
                        required={input.name !== "billingPostalCode"}
                      />
                    </div>
                  )}
                </form.Field>
              ))}
              <form.Subscribe selector={(state) => state.errors}>
                {(errors) =>
                  errors.length > 0 ? (
                    <p role="alert" className="text-sm text-destructive">
                      Choose a card and enter a valid name, email, and two-letter billing country.
                    </p>
                  ) : null
                }
              </form.Subscribe>
              <Button
                data-statsig-name="payment-pilot-approve"
                type="submit"
                variant="primary"
                disabled={approve.isPending || cancel.isPending || !methods.data?.methods.length}
              >
                {approve.isPending ? "Approving…" : "Approve purchase"}
              </Button>
            </form>
          )}
          {!attempt.isError && attempt.data?.actionPath && (
            <Button data-statsig-name="payment-pilot-continue" asChild variant="primary">
              <a
                href={resolveApiPath(attempt.data.actionPath)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Continue with Link
              </a>
            </Button>
          )}
          {attempt.data?.receiptUrl && (
            <Button data-statsig-name="payment-pilot-receipt" asChild>
              <a href={attempt.data.receiptUrl} target="_blank" rel="noopener noreferrer">
                View receipt
              </a>
            </Button>
          )}
          {attempt.data && !complete && (
            <Button
              data-statsig-name="payment-pilot-cancel"
              variant="outline"
              disabled={approve.isPending || cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              {cancel.isPending ? "Cancelling…" : "Cancel purchase"}
            </Button>
          )}
          <Button
            data-statsig-name="payment-pilot-refresh"
            variant="ghost"
            disabled={attempt.isFetching}
            onClick={() => void attempt.refetch()}
          >
            Check purchase status
          </Button>
        </CardContent>
      </Card>
    </main>
  );
};
